import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { trackListingActivated } from '../services/analytics/marketplaceEvents';
import { getLeadByExternalIdQueryKey } from '../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePostLeadByExternalIdActivate } from '../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdActivate';
import { logger } from '../services/sentry';
import { createErrorToast } from '../utils/toast';
import useManualPayoffRequestFallback from './useManualPayoffRequestFallback';

type Missing = 'payoff' | 'conditionReport';

type UseListingSubmitArgs = {
  dealId: string;
  externalId: string;
  onListed?: () => void;
  showErrorToast?: boolean;
};

const buildErrorMessage = (missing: Missing[] | undefined): string => {
  if (missing?.includes('conditionReport')) {
    return 'Finish your condition report before listing.';
  }
  if (missing?.includes('payoff')) {
    return "We're missing payoff details. Please refresh and try again.";
  }
  return "Couldn't list your car. Please try again.";
};

const useListingSubmit = ({
  dealId,
  externalId,
  onListed,
  showErrorToast = false,
}: UseListingSubmitArgs) => {
  const activate = usePostLeadByExternalIdActivate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const requestPayoffIfMissing = useManualPayoffRequestFallback({ dealId, externalId });

  const handleListed = () => {
    onListed?.();
    queryClient
      .invalidateQueries({ queryKey: getLeadByExternalIdQueryKey({ externalId }) })
      .catch((invalidateErr) => {
        logger.error('useListingSubmit', 'lead query invalidation failed', '', invalidateErr);
      });
  };

  const submit = async (askingPrice?: number): Promise<boolean> => {
    let listed = true;
    try {
      await activate.mutateAsync({ externalId });
      trackListingActivated(dealId, askingPrice);
      handleListed();
    } catch (err) {
      const status = (err as { status?: number })?.status ?? 0;
      if (status === 409) {
        handleListed();
      } else {
        listed = false;
        if (status !== 422) {
          logger.error('useListingSubmit', 'activate failed', '', err);
        }
        if (showErrorToast) {
          const missing = (err as { body?: { missing?: Missing[] } })?.body?.missing;
          toast(createErrorToast({ errorMessage: buildErrorMessage(missing) }));
        }
      }
    }

    await requestPayoffIfMissing();
    return listed;
  };

  return { submit, isSubmitting: activate.isPending };
};

export default useListingSubmit;
